/**
 * Item display helpers — UI shows names only, never raw item IDs.
 */
import { getItems, getItem, getUoms } from '../api/inventoryMaster';
import { getTaxMasterByHsn } from '../api/taxMaster';

export const EMPTY_ITEM_LABEL = '—';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Normalize API list responses to a plain array. */
export function normalizeItemList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export function getItemId(item) {
  return item?.item_id ?? item?.id ?? '';
}

export function isUuidLike(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}

function uomList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function uomCodeById(uoms) {
  const map = new Map();
  uomList(uoms).forEach((u) => {
    const id = u.uom_id || u.id;
    if (!id) return;
    const code = u.uom_code || u.code || u.uom_name || u.name || '';
    if (code) map.set(String(id), code);
  });
  return map;
}

/** Purchase UOM code for a catalogue item. Falls back to base UOM. */
export function purchaseUomCodeOf(item = {}, catalog) {
  const direct = item.purchase_uom_code || item.purchase_uom?.uom_code;
  if (direct && !isUuidLike(String(direct))) return String(direct);
  const id = getItemId(item);
  if (id && catalog) {
    return catalog.getPurchaseUomCode?.(id) || catalog.getBaseUomCode?.(id) || '';
  }
  const base = item.base_uom_code || item.base_uom?.uom_code || item.uom_code || '';
  return base && !isUuidLike(String(base)) ? String(base) : '';
}

const GST_SELECT_SLABS = [0, 5, 12, 18, 28];

/** Item-master GST as a select value (`"12"`), or `''` when the item has none. */
export function itemGstPercent(item) {
  if (item == null) return '';
  const raw = item.gst_rate ?? item.tax_percent ?? item.gst_percent ?? item.gst;
  if (raw === '' || raw == null) return '';
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return '';
  const slab = GST_SELECT_SLABS.find((s) => Math.abs(s - n) < 0.051);
  return String(slab != null ? slab : n);
}

/** Item-master GST, then Tax Master by HSN — list payloads often omit gst_rate. */
export async function resolveItemGst(itemId, item) {
  const direct = itemGstPercent(item);
  if (direct && direct !== '0') return direct;
  const hsnFromItem = item?.hsn_code || item?.hsn || '';
  try {
    if (itemId) {
      const full = await getItem(itemId);
      const fromFull = itemGstPercent(full);
      if (fromFull && fromFull !== '0') return fromFull;
      const hsn = full?.hsn_code || full?.hsn || hsnFromItem;
      if (hsn) {
        const tax = await getTaxMasterByHsn(hsn);
        const fromTax = itemGstPercent(tax);
        if (fromTax) return fromTax;
      }
    } else if (hsnFromItem) {
      const tax = await getTaxMasterByHsn(hsnFromItem);
      const fromTax = itemGstPercent(tax);
      if (fromTax) return fromTax;
    }
  } catch {
    /* item or HSN lookup is best-effort */
  }
  return direct || '';
}

export function stampItemUomCodes(items = [], uoms = []) {
  const byId = uomCodeById(uoms);
  const codeOf = (id) => (id ? (byId.get(String(id)) || '') : '');
  return (items || []).map((item) => {
    const purchaseId = item.default_purchase_uom_id || item.base_uom_id;
    const issueId = item.default_issue_uom_id || item.base_uom_id;
    return {
      ...item,
      base_uom_code: item.base_uom_code || codeOf(item.base_uom_id) || item.base_uom?.uom_code || '',
      purchase_uom_code: item.purchase_uom_code || codeOf(purchaseId) || '',
      issue_uom_code: item.issue_uom_code || codeOf(issueId) || '',
    };
  });
}

/**
 * Resolve the UOM id an item defaults to for a transaction role.
 * purchase -> default_purchase_uom_id, issue -> default_issue_uom_id, both
 * fall back to base_uom_id when unset (item master only requires base).
 */
export function defaultUom(item, role) {
  if (!item) return '';
  if (role === 'purchase') return item.default_purchase_uom_id || item.base_uom_id || '';
  if (role === 'issue') return item.default_issue_uom_id || item.base_uom_id || '';
  return item.base_uom_id || '';
}

/** Returns a display-safe name — rejects values that are IDs/UUIDs. */
export function meaningfulItemName(name, itemId) {
  if (!name?.trim()) return null;
  const trimmed = name.trim();
  if (trimmed === itemId || isUuidLike(trimmed)) return null;
  return trimmed;
}

/** Item name only (no code, no ID). */
export function itemNameOnly(item) {
  if (!item) return null;
  const id = getItemId(item);
  return (
    meaningfulItemName(item.item_name, id)
    || meaningfulItemName(item.item_short_name, id)
    || null
  );
}

/** Missing `is_active` is treated as active — same convention as vendors. */
export function isItemActive(item) {
  return item?.is_active !== false;
}

export function filterActiveItems(list) {
  return (Array.isArray(list) ? list : []).filter(isItemActive);
}

export function inactiveItemMessage(item) {
  const name = itemNameOnly(item) || item?.item_code || 'This item';
  return `${name} is inactive and cannot be used.`;
}

/** Map backend "Item '<uuid>' is inactive…" into a name-based message. */
export function formatInactiveItemApiError(err, fallbackItem) {
  const raw = err?.message || '';
  if (/inactiv/i.test(raw)) return inactiveItemMessage(fallbackItem);
  return raw || 'Failed to save. Please try again.';
}

function resolveFromArray(row, items) {
  if (!row) return EMPTY_ITEM_LABEL;
  const itemId = row.item_id || getItemId(row);
  const fromRow = meaningfulItemName(row.item_name, itemId);
  if (fromRow) return fromRow;
  if (!itemId || !items?.length) return EMPTY_ITEM_LABEL;
  const master = items.find(i => getItemId(i) === itemId);
  return itemNameOnly(master) || EMPTY_ITEM_LABEL;
}

/**
 * Resolve a user-visible item name from a line row.
 * @param {object} row — API line with item_id / item_name
 * @param {object[]|ReturnType<createItemCatalog>} catalog — array or indexed catalog
 */
export function resolveItemDisplayName(row, catalog = []) {
  if (catalog && typeof catalog.resolve === 'function') {
    return catalog.resolve(row);
  }
  return resolveFromArray(row, catalog);
}

/** For select / autocomplete option labels — name only. */
export function itemOptionLabel(item) {
  return itemNameOnly(item) || EMPTY_ITEM_LABEL;
}

export function itemOptionLabelWithBatch(item) {
  const name = itemOptionLabel(item);
  const batch = item?.batch_number;
  return batch ? `${name} [${batch}]` : name;
}

/**
 * Build an indexed catalog for fast repeated lookups (grids, PDFs, etc.).
 */
export function createItemCatalog(items = []) {
  const list = normalizeItemList(items);
  const byId = new Map();

  for (const item of list) {
    const id = getItemId(item);
    if (id) byId.set(id, item);
  }

  function resolveById(itemId) {
    if (!itemId) return EMPTY_ITEM_LABEL;
    return itemNameOnly(byId.get(itemId)) || EMPTY_ITEM_LABEL;
  }

  function resolve(row) {
    if (!row) return EMPTY_ITEM_LABEL;
    const itemId = row.item_id || getItemId(row);
    const fromRow = meaningfulItemName(row.item_name, itemId);
    if (fromRow) return fromRow;
    return resolveById(itemId);
  }

  // ── Extended lookup helpers (additive) ─────────────────────
  // Each returns a safe default so callers never have to null-check.

  function getBarcode(id) {
    return byId.get(id)?.primary_barcode || null;
  }

  function getBaseUomCode(id) {
    const row = byId.get(id);
    return row?.base_uom?.uom_code || row?.base_uom_code || '';
  }

  function getPurchaseUomCode(id) {
    const row = byId.get(id);
    if (!row) return '';
    return row.purchase_uom_code || row.base_uom_code || row.base_uom?.uom_code || '';
  }

  function getIssueUomCode(id) {
    return byId.get(id)?.issue_uom_code || '';
  }

  function getPurchaseUomId(id) {
    return defaultUom(byId.get(id), 'purchase');
  }

  function getIssueUomId(id) {
    return defaultUom(byId.get(id), 'issue');
  }

  function isControlled(id) {
    const row = byId.get(id);
    return Boolean(row?.is_controlled_drug || row?.is_narcotic);
  }

  function isBatchTracked(id) {
    return Boolean(byId.get(id)?.is_batch_tracked);
  }

  function isExpiryTracked(id) {
    return Boolean(byId.get(id)?.is_expiry_tracked);
  }

  function getItemCode(id) {
    return byId.get(id)?.item_code || '';
  }

  // ItemResponse denormalises the classification chain onto flat *_name fields
  // (item_service._enrich_item_responses); the nested objects these also read
  // are the older shape some callers still send.
  function getItemClass(id) {
    const row = byId.get(id);
    const ic = row?.item_class;
    return row?.item_class_name ?? ic?.class_name ?? ic?.item_class_name ?? ic?.name ?? '';
  }

  function getMaterialGroup(id) {
    const row = byId.get(id);
    const mg = row?.material_group;
    return row?.material_group_name ?? mg?.group_name ?? mg?.material_group_name ?? mg?.name ?? '';
  }

  function getGstRate(id) {
    return itemGstPercent(byId.get(id));
  }

  return {
    items: list,
    byId,
    resolve,
    resolveById,
    optionLabel: itemOptionLabel,
    optionLabelWithBatch: itemOptionLabelWithBatch,
    getBarcode,
    getBaseUomCode,
    getPurchaseUomCode,
    getIssueUomCode,
    getPurchaseUomId,
    getIssueUomId,
    isControlled,
    isBatchTracked,
    isExpiryTracked,
    getItemCode,
    getItemClass,
    getMaterialGroup,
    getGstRate,
  };
}

/** Copy batch/expiry tracking flags from the item catalog onto a GRN line. */
export function enrichLineWithItemTracking(line, catalog) {
  if (!line?.item_id || !catalog?.byId) return line;
  const master = catalog.byId.get(line.item_id);
  if (!master) return line;
  return {
    ...line,
    is_batch_tracked: Boolean(master.is_batch_tracked),
    is_expiry_tracked: Boolean(master.is_expiry_tracked),
    item_code: master.item_code || line.item_code || '',
  };
}

let cachedCatalog = null;
let loadPromise = null;

export function invalidateItemCatalogCache() {
  cachedCatalog = null;
  loadPromise = null;
}

/** Shared cache — one GET /inventory/items/ for the whole inventory module. */
export function fetchItemCatalog({ force = false } = {}) {
  if (!force && cachedCatalog) return Promise.resolve(cachedCatalog);
  if (!force && loadPromise) return loadPromise;

  loadPromise = Promise.all([
    getItems(),
    getUoms().catch(() => []),
  ])
    .then(([rawItems, rawUoms]) => {
      const items = stampItemUomCodes(normalizeItemList(rawItems), rawUoms);
      cachedCatalog = createItemCatalog(items);
      return cachedCatalog;
    })
    .catch(err => {
      console.error('[itemDisplay] catalog fetch failed:', err);
      // Reset so the next call retries instead of returning a stale null promise
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}